import { ReactNode, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import { mobile } from "./media";
import { backdrop, overlayPanel, useMotion } from "./motion";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message?: string | ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "primary";
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  variant = "danger",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { reduce } = useMotion();

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", handleKey);
    dialogRef.current?.focus();
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onCancel]);

  return (
    <AnimatePresence>
      {open ? (
        <Overlay
          key="overlay"
          variants={backdrop(reduce)}
          initial="hidden"
          animate="show"
          exit="exit"
          onClick={onCancel}
        >
          <Dialog
            ref={dialogRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            aria-describedby={message ? "confirm-message" : undefined}
            tabIndex={-1}
            variants={overlayPanel(reduce)}
            initial="hidden"
            animate="show"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <Title id="confirm-title">{title}</Title>
            {message ? <Message id="confirm-message">{message}</Message> : null}
            <Actions>
              <DialogBtn onClick={onCancel}>{cancelLabel}</DialogBtn>
              <DialogBtn $danger={variant === "danger"} $primary onClick={onConfirm}>
                {confirmLabel}
              </DialogBtn>
            </Actions>
          </Dialog>
        </Overlay>
      ) : null}
    </AnimatePresence>
  );
};

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: ${({ theme }) => theme.zIndex.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.state.scrim};
`;

const Dialog = styled(motion.div)`
  background: ${({ theme }) => theme.colors.background.liquidGlass};
  backdrop-filter: blur(36px) saturate(180%) brightness(1.05);
  -webkit-backdrop-filter: blur(36px) saturate(180%) brightness(1.05);
  border: 1px solid ${({ theme }) => theme.colors.background.liquidGlassBorder};
  border-radius: ${({ theme }) => theme.radius.xl}px;
  box-shadow: ${({ theme }) => theme.colors.background.liquidGlassHighlight},
    ${({ theme }) => theme.colors.shadow.high};
  padding: ${({ theme }) => theme.spacing(3)};
  max-width: 400px;
  width: calc(100% - ${({ theme }) => theme.spacing(4)});
  outline: none;

  ${mobile} {
    max-width: calc(100% - ${({ theme }) => theme.spacing(4)});
  }
`;

const Title = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  font-size: ${({ theme }) => theme.typography.h3};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 700;
`;

const Message = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(2.5)};
  font-size: ${({ theme }) => theme.typography.body};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const DialogBtn = styled.button<{ $primary?: boolean; $danger?: boolean }>`
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2.5)}`};
  border-radius: ${({ theme }) => theme.radius.pill}px;
  font-size: ${({ theme }) => theme.typography.body};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-weight: 600;
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast} ease,
    opacity ${({ theme }) => theme.motion.fast} ease;

  ${({ $primary, $danger, theme }) =>
    $primary
      ? css`
          background: ${$danger ? theme.colors.accent.danger : theme.colors.accent.primary};
          color: ${theme.colors.text.inverse};
          border: 1px solid ${$danger ? theme.colors.accent.danger : theme.colors.accent.primaryHover};
          &:hover { opacity: 0.85; }
        `
      : css`
          background: ${theme.colors.background.glass};
          color: ${theme.colors.text.primary};
          border: 1px solid ${theme.colors.background.glassBorder};
          &:hover { background: ${theme.colors.background.surfaceAlt}; }
        `}

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${({ theme }) => theme.spacing(0.5)}
      ${({ theme }) => theme.colors.border.focus};
  }
`;
